import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setItemDiscount,
  overridePrice,
  setLineTaxes,
  removeItem,
  expandLine,
  removeItemCoupon,
  restoreLineDiscounts, overrideRestorePrice } from "../../../Redux/features/WebRegister/cartSlice";
import ItemCouponModal from "./ItemCouponModal";
import { canRegister } from "../permissions";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import { fmt } from "../uiUtils";
import { roundHalfUp, toNum } from "../engine";
import { opCompanyLabel } from "../engine/dezi";
import { useRegTheme } from "../theme";
import NumericInput from "../NumericInput";

/** "Loyalty Fund Allowances · USSTC" — the allowance type and the manufacturer funding it. */
const deziAllowanceMeta = (b) =>
  [b && b.discountType, opCompanyLabel(b && b.opCompanyCode)].filter(Boolean).join(" · ");

// Line-item editor — port of layout_cart_item2.xml (the inline panel Android shows when a cart line
// is tapped): name + delete, price (tap to override), quantity stepper, adjustment, subtotal,
// discount, per-line tax breakdown, total item cost, and $ Discount / % Discount / Add-Remove Tax /
// Price Override actions. Edits apply live; Cancel reverts to the state on open, Done keeps them.
export default function LineActionsModal({ line: initialLine, taxMap, onClose }) {
  const t = useRegTheme();
  const dispatch = useDispatch();
  const orderId = initialLine.orderId;
  const line = useSelector((st) => st.webRegisterCart.lines.find((l) => l.orderId === orderId));
  const allLines = useSelector((st) => st.webRegisterCart.lines);
  const isOutdoor = useSelector((st) => st.webRegisterCart.isOutdoor);
  const loyaltyAmount = useSelector((st) => st.webRegisterCart.loyaltyAmount);
  // QV-4738 item-coupon guard inputs (Android buttonItemCoupon chain).
  const couponTotal = useSelector((st) => st.webRegisterCart.couponTotal);
  const customTotal = useSelector((st) => st.webRegisterCart.customTotal);
  const paymentsCount = useSelector((st) => (st.webRegisterCart.payments || []).length);
  const giftCardsCount = useSelector((st) => (st.webRegisterCart.giftCards || []).length);

  // Android onOrderDelete guards: no item removal mid-split, or while OTD / a loyalty payment / a
  // redeemed gift card is applied.
  const deleteBlocked = () => {
    if (paymentsCount > 0) {
      ToastifyAlert("Split payment in progress", "error");
      return true;
    }
    if (isOutdoor) {
      ToastifyAlert("Remove the OTD price first", "error");
      return true;
    }
    if (Number(loyaltyAmount) > 0) {
      ToastifyAlert("Remove the loyalty payment first", "error");
      return true;
    }
    // A REDEEMED gift card (a tender, not a sold "Gift Card(...)" line) is applied against the
    // current cart total; deleting a line while it is still applied lets the redeemed amount exceed
    // the remaining total and drives the grand total negative (Android ae13e4cdf9 — the same guard
    // loyalty already had).
    if (giftCardsCount > 0) {
      ToastifyAlert("Remove the gift card first", "error");
      return true;
    }
    return false;
  };

  const snap = useRef(null);
  if (line && !snap.current) {
    snap.current = {
      orderCount: line.orderCount,
      manualOverridePrice: line.manualOverridePrice,
      // the CASH override (dual price inflates it in the slice) — Cancel must restore this, never the card price
      manualOverrideBase: line.manualOverrideBase,
      taxList: (line.taxList || []).map((x) => ({ ...x })),
      // manual + item-coupon snapshot (QV-4738) — restored wholesale on Cancel
      manualDiscountAmt: line.manualDiscountAmt,
      manualDiscountPercent: line.manualDiscountPercent,
      couponDiscountName: line.couponDiscountName,
      couponDiscountType: line.couponDiscountType,
      couponDiscountValue: line.couponDiscountValue,
      couponMaxDiscount: line.couponMaxDiscount,
      couponCanCombine: line.couponCanCombine,
      couponAltriaFunded: line.couponAltriaFunded,
      couponPromotionCode: line.couponPromotionCode,
    };
  }
  useEffect(() => {
    if (!line) onClose();
  }, [line, onClose]);

  const [panel, setPanel] = useState(null); // null | amt | pct | override | tax
  const [showCouponPicker, setShowCouponPicker] = useState(false); // QV-4738 item-coupon dialog
  const [val, setVal] = useState(0);
  // Editor preview quantity (Android modelEdit.orderCount). The cart row stays orderCount=1; on Done
  // the line is exploded into `editQty` unit rows (expandMultiplier). All editor math uses editQty.
  const [editQty, setEditQty] = useState(() => Math.max(1, toNum(initialLine.orderCount)));

  if (!line) return null;

  // ── breakdown (mirrors the engine so the numbers match the cart) ──
  const unit = toNum(line.orderUnitNewPrice) > 0 ? toNum(line.orderUnitNewPrice) : toNum(line.orderUnitPrice);
  const catalog = toNum(line.catalogUnitPrice);
  const qty = editQty; // editor preview count → expanded to unit rows on Done
  // A line discounted by a promotion (BOGO or Mix&Match) cannot take a manual discount / override /
  // item coupon — Android greys those buttons (BOGO) / blocks them (bulk). This includes the PAID
  // row of a BOGO set (Android: bogoIdentifier != 0 && != 1 → "bogo already applied"): the web marks
  // every set member with bogoIdentifier=setId>0, and the discount/coupon sentinel never survives a
  // recalc, so bogoIdentifier > 0 here always means real BOGO membership.
  const isBogoMember = !!line.isBoGoProduct || toNum(line.bogoIdentifier) > 0;
  // ⚠️ Mix&Match locks on the LINE'S OWN state, never on "the product takes part in a deal". After a
  // max_qty cap splits a group, the overflow unit keeps the deal id but is deliberately NOT
  // bulk-priced — it is an ordinary full-price line and a manual discount on it must be allowed
  // (Android fix: the old qty-based check reported it as deal-locked and refused).
  const promoLocked = !!(isBogoMember || line.isBulkPriceApplied);
  // QV-7124 rule 3 — on a manufacturer-discounted line, manual $/% discount, the item coupon AND the
  // price override are ALL blocked. Android originally allowed the override, which let a cashier
  // price a line below the manufacturer discount and collapse the sale to $0.00; blocking it at
  // source (their fix "d3") is what closed that hole.
  const deziLocked = !!line.isDeziUpcLine;
  const subtotal = roundHalfUp(unit * qty);
  const overrideActive = toNum(line.manualOverridePrice) > 0;
  const adjustment = overrideActive ? roundHalfUp((catalog - unit) * qty) : 0; // + = cheaper, − = markup
  // QV-4738: manual discount and the item coupon are shown as TWO separate rows (Android
  // displayCartItem). Manual per-unit here mirrors Android's display math (plain ×, no truncation).
  const manualPct = toNum(line.manualDiscountPercent);
  const manualAmt = toNum(line.manualDiscountAmt);
  const hasDiscount = manualPct > 0 || manualAmt > 0;
  const manualPerUnit = manualPct > 0 ? (unit * manualPct) / 100 : manualAmt;
  const itemDiscAmt = manualPerUnit * qty; // Android displayCartItem: manual per-unit × count (no truncation in display)
  const discountLabel = manualPct > 0 ? `${manualPct}% OFF` : `${fmt(manualAmt)}/ea off`;
  const hasItemCoupon = !!line.couponDiscountName;
  const couponRowAmt = toNum(line.couponDiscountFinalAmt) * qty;
  const net = toNum(line.jsonNewPrice); // per-unit line net (after item + order-level discounts)
  const taxRows = (line.taxList || []).map((tx) => ({
    name: tx.displayName || tx.title,
    percent: toNum(tx.percent),
    amount: roundHalfUp((net * toNum(tx.percent)) / 100) * qty, // scaled to the preview qty
  }));
  const taxTotal = taxRows.reduce((s, r) => s + r.amount, 0);
  const totalItemCost = roundHalfUp(net * qty + taxTotal);

  const appliedIds = new Set((line.taxList || []).map((x) => String(x.id)));
  const allTaxes = Array.from(taxMap ? taxMap.values() : []).filter((x) => x && x.id != null && String(x.id).trim() !== "");

  // ── actions ── (qty changes are local preview; committed on Done via expandLine)
  const otherUnits = () =>
    allLines
      .filter((x) => x.orderId !== orderId && x.orderProductId === line.orderProductId && (x.variantId || "") === (line.variantId || ""))
      .reduce((s2, x) => s2 + toNum(x.orderCount), 0);
  const inc = () => setEditQty((q) => q + 1);
  const dec = () => setEditQty((q) => Math.max(1, q - 1)); // Android editor minus stops at 1 (delete via trash)
  const openPanel = (p, perm) => {
    if (deziLocked && (p === "amt" || p === "pct" || p === "override")) {
      return ToastifyAlert("This item has a manufacturer discount — no other discount or price change can be applied.", "error");
    }
    // $ discount / % discount / price override are disabled on promotion-discounted lines.
    if (promoLocked && (p === "amt" || p === "pct" || p === "override")) {
      return ToastifyAlert(
        isBogoMember ? "Discount can't be changed while a BOGO discount is applied" : "Remove the Mix & Match price first",
        "error"
      );
    }
    // QV-4738: a non-combinable item coupon blocks the manual discount buttons (Android
    // buttonDiscount/buttonDiscountPercent pre-guard).
    if ((p === "amt" || p === "pct") && hasItemCoupon && !line.couponCanCombine) {
      return ToastifyAlert("A coupon is already applied on this item. Remove it before adding a discount.", "error");
    }
    // Android buttonDiscount / buttonDiscountPercent, in order: split payment, then gift card, then
    // "a discount is already on this line / order". Note the split gate is on the DISCOUNT buttons
    // only — Android's buttonOverridePrice and buttonTax carry no isSplitPayment() check, so the
    // editor stays usable for a price change mid-split exactly as it does on the POS (QA: Varun
    // 2026-08-28 "even after doing split payment i am able to open and edit line item").
    if (p === "amt" || p === "pct") {
      if (paymentsCount > 0)
        return ToastifyAlert("Split payment is in progress, discount should apply before any type of payment.", "error");
      if (giftCardsCount > 0)
        return ToastifyAlert("Discount must applied before Gift Card need to be applied.", "error");
    }
    // Android buttonOverridePrice / tvTapToEditPrice / tvPriceValue: a price override is refused
    // while ANY discount sits on the line or the order — order-level discount, a manual $ / %
    // discount, or (QV-4738) an item coupon — and while a gift card is redeemed. The web has one
    // entry point for all three Android controls, so it checks the union of their conditions:
    // manualDiscount* (buttonOverridePrice) plus the effective orderDiscount* (tvPriceValue).
    // (QA: Varun 2026-08-28 "able to edit price even after applying amount discount, in POS it says
    // please remove discount first".)
    if (p === "override") {
      if (
        toNum(couponTotal) > 0 ||
        toNum(customTotal) > 0 ||
        toNum(line.manualDiscountAmt) > 0 ||
        toNum(line.manualDiscountPercent) > 0 ||
        toNum(line.orderDiscount) > 0 ||
        toNum(line.orderDiscountPercent) > 0 ||
        hasItemCoupon
      ) {
        return ToastifyAlert("Remove the Discount first!", "error");
      }
      if (giftCardsCount > 0)
        return ToastifyAlert("Discount must applied before Gift Card need to be applied.", "error");
    }
    if (perm && !canRegister(perm)) return ToastifyAlert("Access denied", "error");
    setVal(p === "override" ? unit : 0);
    setPanel(panel === p ? null : p);
  };

  // QV-4738 Apply Coupon — Android buttonItemCoupon guard chain, in ORDER: permission → split
  // payment → gift card → lottery → order-level discount → Mix&Match/bulk → override → BOGO → dialog.
  const openCouponPicker = () => {
    // Applying a coupon is a COUPON action, not an item-discount one — Android moved this gate from
    // MENU_ITEM_DISCOUNT to the same permission the cart-level Coupons button uses.
    if (!canRegister("MENU_APPLY_COUPONS")) return ToastifyAlert("Access denied", "error");
    if (deziLocked)
      return ToastifyAlert("This item has a manufacturer discount — a coupon can't be applied to it.", "error");
    if (paymentsCount > 0)
      return ToastifyAlert("Split payment is in progress, discount should apply before any type of payment.", "error");
    if (giftCardsCount > 0)
      return ToastifyAlert("Discount must applied before Gift Card need to be applied.", "error");
    if (line.isLotteryTicket === "1" || line.isLotteryPayout || line.isScratcherPayout)
      return ToastifyAlert("Coupon cannot be applied to lottery products!", "error");
    // Android e587a0b241 (IPF:7703): exclude_coupons_store also blocks ITEM coupons on that category.
    if (line.isCouponEligible === false)
      return ToastifyAlert("This item's category is excluded from coupons, so a coupon cannot be applied.", "error");
    if (toNum(couponTotal) > 0 || toNum(customTotal) > 0)
      return ToastifyAlert("A discount has already been applied to this order.", "error");
    if (line.isBulkPriceApplied) return ToastifyAlert("Mix N' Match Pricing already applied.", "error");
    if (toNum(line.manualOverridePrice) > 0) return ToastifyAlert("Remove the Override Price first!", "error");
    // Android QV-6979: FREE row (isBoGoProduct) AND the PAID row of a BOGO set (bogoIdentifier set)
    if (isBogoMember) return ToastifyAlert("Bogo already applied", "error");
    setShowCouponPicker(true);
  };
  // Android rule: Mix&Match (bulk) price and manual adjustments are mutually exclusive —
  // "please remove … since bulk price already applied" (blocked up-front here).
  const bulkBlocked = (what) => {
    if (line.isBulkPriceApplied) {
      ToastifyAlert(`Mix & Match price is applied — remove it before a ${what}`, "error");
      return true;
    }
    return false;
  };
  const applyAmtDiscount = () => {
    if (bulkBlocked("discount")) return;
    const v = toNum(val);
    if (v <= 0) return ToastifyAlert("Enter a valid amount", "error");
    if (v >= unit) return ToastifyAlert("Amount should be less than the per-item price", "error"); // Android: amount_should_be_less_than_per_item
    dispatch(setItemDiscount({ orderId, amount: v, percent: 0 }));
    setPanel(null);
  };
  const applyPctDiscount = () => {
    if (bulkBlocked("discount")) return;
    const v = toNum(val);
    if (v <= 0 || v > 100) return ToastifyAlert("Enter a percent between 0 and 100", "error"); // Android: 0 < pct ≤ 100
    dispatch(setItemDiscount({ orderId, amount: 0, percent: v }));
    setPanel(null);
  };
  // The ✕ next to the discount row is the web's only way to REMOVE a line discount; on Android the
  // same action lives behind buttonDiscount, so the split gate covers it there. Mirror it, or a
  // cashier could drop a discount after a tender was already taken and move the total under it.
  // (clearOverride has no such gate — Android's buttonOverridePrice carries no split check either.)
  const clearDiscount = () => {
    if (paymentsCount > 0)
      return ToastifyAlert("Split payment is in progress, discount should apply before any type of payment.", "error");
    dispatch(setItemDiscount({ orderId, amount: 0, percent: 0 }));
  };
  const applyOverride = () => {
    if (bulkBlocked("price override")) return;
    const v = toNum(val);
    if (v <= 0) return ToastifyAlert("Enter a price", "error");
    dispatch(overridePrice({ orderId, price: v }));
    setPanel(null);
  };
  const clearOverride = () => dispatch(overridePrice({ orderId, price: 0 }));
  const toggleTax = (tax) => {
    const id = String(tax.id);
    let next;
    if (appliedIds.has(id)) {
      next = (line.taxList || []).filter((x) => String(x.id) !== id);
    } else {
      next = [...(line.taxList || []), { id: tax.id, title: tax.title, displayName: tax.displayname || tax.displayName || null, percent: tax.percent }];
    }
    dispatch(setLineTaxes({ orderId, taxList: next }));
  };
  const cancel = () => {
    // Revert the live-dispatched edits (discount/coupon/override/tax); qty was preview-only.
    const s = snap.current;
    if (s) {
      dispatch(overridePrice({ orderId, price: overrideRestorePrice(s) }));
      dispatch(restoreLineDiscounts({ orderId, snapshot: s })); // manual + item coupon wholesale
      dispatch(setLineTaxes({ orderId, taxList: s.taxList }));
    }
    onClose();
  };
  // Android buttonDone: validate, then commit the qty by exploding into editQty unit rows.
  const done = () => {
    if (line.isBulkPriceApplied && (toNum(line.orderDiscount) > 0 || toNum(line.orderDiscountPercent) > 0))
      return ToastifyAlert("Remove the item discount — a Mix & Match price is applied", "error");
    if (line.isBulkPriceApplied && toNum(line.manualOverridePrice) > 0)
      return ToastifyAlert("Remove the override price — a Mix & Match price is applied", "error");
    const cap = toNum(line.purchaseQtyCap);
    if (cap > 0 && otherUnits() + editQty > cap)
      return ToastifyAlert(`This product is limited to ${cap} per transaction`, "error");
    if (editQty > 1) dispatch(expandLine({ orderId, count: editQty }));
    onClose();
  };

  const s = styles(t);
  return (
    // NO outside-click close: the editor holds live edits — it only closes via ✕/Cancel (revert) or Done.
    <div className="qv-dlg-overlay" style={s.overlay}>
      <div className="qv-dlg-card" style={s.card} onClick={(e) => e.stopPropagation()}>
        <div style={s.head}>
          <span style={s.title}>{line.orderName}</span>
          <span style={s.headBtns}>
            {/* BOGO-discounted rows have no delete option (Android) — remove the paid row instead. */}
            {!line.isBoGoProduct && (
              <button
                style={s.trash}
                title="Remove item"
                onClick={() => {
                  if (deleteBlocked()) return;
                  dispatch(removeItem(orderId));
                  onClose();
                }}
              >
                {/* inline SVG so the bin is reliably RED (emoji glyphs ignore CSS color) */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#DC2626" aria-hidden="true">
                  <path d="M9 3h6a1 1 0 0 1 1 1v1h5v2H3V5h5V4a1 1 0 0 1 1-1zm-3 5h12l-.9 12.1a2 2 0 0 1-2 1.9H8.9a2 2 0 0 1-2-1.9L6 8zm4 2v9h2v-9h-2zm4 0v9h2v-9h-2z" />
                </svg>
              </button>
            )}
            {/* blue ✕ — closes the editor like Cancel (reverts live edits) */}
            <button style={s.xClose} title="Close" onClick={cancel}>✕</button>
          </span>
        </div>

        <div style={s.body}>
          {/* Price */}
          <Row t={t} label="Price">
            <button style={s.valBox} onClick={() => openPanel("override", "MENU_CHANGE_PRICE")}>{fmt(unit)}</button>
          </Row>
          {panel === "override" && (
            <InlinePanel t={t} value={val} onChange={setVal} placeholder={fmt(line.basePrice != null ? line.basePrice : catalog)} onApply={applyOverride} applyLabel="Set price" />
          )}

          {/* Quantity */}
          <Row t={t} label="Quantity">
            <div style={s.stepper}>
              <button
                style={line.isBoGoProduct ? { ...s.stepBtn, opacity: 0.4, cursor: "not-allowed" } : s.stepBtn}
                onClick={() => {
                  if (line.isBoGoProduct) return ToastifyAlert("Quantity can't be changed while a BOGO discount is applied", "error");
                  dec();
                }}
              >−</button>
              <span style={s.qty}>{editQty}</span>
              <button
                style={line.isBoGoProduct ? { ...s.stepBtn, opacity: 0.4, cursor: "not-allowed" } : s.stepBtn}
                onClick={() => {
                  if (line.isBoGoProduct) return ToastifyAlert("Quantity can't be changed while a BOGO discount is applied", "error");
                  // Caps count TOTAL units after this edit commits: other sibling rows + the new editQty.
                  const next = otherUnits() + editQty + 1;
                  if (line.stockTracked && line.maxStock > 0 && next > line.maxStock) return ToastifyAlert("Reached maximum quantity", "error");
                  const cap = toNum(line.purchaseQtyCap);
                  if (cap > 0 && next > cap) return ToastifyAlert(`This product is limited to ${cap} per transaction`, "error"); // Android: product_limit_per_transaction
                  inc();
                }}
              >+</button>
            </div>
          </Row>

          {/* Mix & Match (bulk) price active — informational (removal is automatic when qty drops) */}
          {line.isBulkPriceApplied && (
            <Row t={t} label={<span style={{ color: "#16A34A", fontWeight: 500 }}>Mix &amp; Match</span>} muted>
              <span>−{fmt(Math.max(roundHalfUp((catalog - unit) * qty), 0))}</span>
            </Row>
          )}

          {/* Adjustment (price override diff) */}
          {overrideActive && (
            <Row t={t} label="Adjustment" muted>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {adjustment >= 0 ? "−" : "+"}{fmt(Math.abs(adjustment))}
                <button style={s.clear} onClick={clearOverride}>✕</button>
              </span>
            </Row>
          )}

          <div style={s.divider} />
          <Row t={t} label="Subtotal"><span>{fmt(subtotal)}</span></Row>

          {/* Manual discount (item coupon shows separately below — Android groupDiscount/groupCoupon) */}
          {hasDiscount && (
            <Row t={t} label={<span style={{ color: "#16A34A", fontWeight: 500 }}>{discountLabel}</span>}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                −{fmt(itemDiscAmt)}
                <button style={s.clear} onClick={clearDiscount}>✕</button>
              </span>
            </Row>
          )}

          {/* QV-7124 manufacturer discount — no ✕: it is funded by the manufacturer and set by
              mKonnekt, so the cashier cannot remove or edit it. Breakdown rows name each allowance. */}
          {toNum(line.deziItemDiscountAmt) > 0 && (
            <>
              <Row t={t} label={<span style={{ color: "#16A34A", fontWeight: 500 }}>Manufacturer Discount</span>}>
                <span>−{fmt(toNum(line.deziItemDiscountAmt) * qty)}</span>
              </Row>
              {(line.deziBreakdown || []).map((b, i) => (
                <Row
                  key={i}
                  t={t}
                  label={
                    <span style={{ fontSize: 12 }}>
                      {b.name}
                      {deziAllowanceMeta(b) && (
                        <span style={{ opacity: 0.75 }}> ({deziAllowanceMeta(b)})</span>
                      )}
                    </span>
                  }
                  muted
                >
                  <span style={{ fontSize: 12 }}>−{fmt(b.amount)}</span>
                </Row>
              ))}
            </>
          )}

          {/* Item coupon (QV-4738) — removing it keeps any manual discount (Android ivCoupon) */}
          {hasItemCoupon && (
            <Row t={t} label={<span style={{ color: "#16A34A", fontWeight: 500 }}>Coupon({line.couponDiscountName})</span>}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                −{fmt(couponRowAmt)}
                <button style={s.clear} onClick={() => dispatch(removeItemCoupon(orderId))}>✕</button>
              </span>
            </Row>
          )}

          {/* Per-line tax breakdown */}
          {taxRows.map((tx, i) => (
            <Row key={i} t={t} label={`${tx.name}${tx.percent ? ` - ${tx.percentRaw || tx.percent}%` : ""}`} muted>
              <span>{fmt(tx.amount)}</span>
            </Row>
          ))}

          <div style={s.divider} />
          <Row t={t} label={<b>Total Item Cost</b>}><b>{fmt(totalItemCost)}</b></Row>
        </div>

        {/* inline panels for the action buttons */}
        {panel === "amt" && <InlinePanel t={t} value={val} onChange={setVal} placeholder="1.00" onApply={applyAmtDiscount} applyLabel="Apply $ discount" />}
        {panel === "pct" && <InlinePanel t={t} value={val} onChange={setVal} placeholder="10" onApply={applyPctDiscount} applyLabel="Apply % discount" />}
        {panel === "tax" && (
          <div style={s.taxPanel}>
            {allTaxes.length === 0 && <div style={s.muted}>No taxes configured</div>}
            {allTaxes.map((tax) => {
              const on = appliedIds.has(String(tax.id));
              return (
                <button key={String(tax.id)} style={{ ...s.taxOpt, borderColor: on ? t.accent : t.cardBorder, color: on ? t.accent : t.text }} onClick={() => toggleTax(tax)}>
                  <span>{on ? "☑" : "☐"} {tax.displayname || tax.title}{tax.percent ? ` (${String(tax.percent).trim()}%)` : ""}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Add/Remove Tax stays available on a manufacturer-discounted line — only the DISCOUNT and
            PRICE controls are locked (Android greys exactly these four). */}
        <div style={s.actions}>
          <button style={promoLocked || deziLocked ? s.actOff : s.act} onClick={() => openPanel("amt", "MENU_DISCOUNT")}>$ Discount</button>
          <button style={promoLocked || deziLocked ? s.actOff : s.act} onClick={() => openPanel("pct", "MENU_DISCOUNT")}>% Discount</button>
          <button style={s.act} onClick={() => openPanel("tax", "MENU_ADD_REMOVE_TAX")}>Add/Remove Tax</button>
          <button style={promoLocked || deziLocked ? s.actOff : s.act} onClick={() => openPanel("override", "MENU_CHANGE_PRICE")}>Price Override</button>
          {/* QV-4738 item-level coupon (Android buttonItemCoupon; greyed on BOGO/bulk like the others) */}
          <button style={promoLocked || deziLocked ? s.actOff : s.act} onClick={openCouponPicker}>Apply Coupon</button>
        </div>
        <div style={s.foot}>
          <button style={s.cancel} onClick={cancel}>Cancel</button>
          <button style={s.done} onClick={done}>Done</button>
        </div>
        {showCouponPicker && (
          <ItemCouponModal orderId={orderId} onClose={() => setShowCouponPicker(false)} />
        )}
      </div>
    </div>
  );
}

function Row({ t, label, children, muted }) {
  const s = styles(t);
  return (
    <div style={s.row}>
      <span style={muted ? s.rowLabelMuted : s.rowLabel}>{label}</span>
      <span style={muted ? s.rowValMuted : s.rowVal}>{children}</span>
    </div>
  );
}

function InlinePanel({ t, value, onChange, onApply, applyLabel }) {
  const s = styles(t);
  return (
    <div style={s.inline}>
      <NumericInput style={s.input} value={value} onChange={onChange} decimals={2} autoFocus />
      <button style={s.inlineApply} onClick={onApply}>{applyLabel}</button>
    </div>
  );
}

const styles = (t) => ({
  overlay: { position: "fixed", inset: 0, background: "rgba(2,6,23,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 },
  card: { width: "100%", maxWidth: 440, maxHeight: "92vh", background: t.card || "#0f1a2f", color: t.text || "#E5EDF7", borderRadius: 16, border: `1px solid ${t.cardBorder || "#1f2c47"}`, overflow: "hidden", display: "flex", flexDirection: "column" },
  head: { padding: "14px 18px", borderBottom: `1px solid ${t.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.surfaceAlt || "#0f1a2f" },
  title: { fontWeight: 800, fontSize: 17 },
  headBtns: { display: "flex", alignItems: "center", gap: 14 },
  trash: { border: "none", background: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" },
  xClose: { border: "none", background: "none", cursor: "pointer", color: "var(--accent)", fontSize: 20, fontWeight: 800, lineHeight: 1, padding: 0 },
  body: { padding: "6px 18px", overflow: "auto" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" },
  rowLabel: { fontSize: 15, fontWeight: 500 },
  rowLabelMuted: { fontSize: 14, color: t.textMuted },
  rowVal: { fontSize: 16, fontWeight: 500 },
  rowValMuted: { fontSize: 14, color: t.textMuted },
  valBox: { minWidth: 120, textAlign: "center", padding: "8px 14px", borderRadius: 10, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text, fontSize: 18, fontWeight: 800, cursor: "pointer" },
  stepper: { display: "flex", alignItems: "center", gap: 12, border: `1px solid ${t.inputBorder}`, borderRadius: 10, padding: "4px 10px", background: t.inputBg },
  stepBtn: { width: 30, height: 30, borderRadius: 8, border: "none", background: "transparent", color: t.text, fontSize: 20, cursor: "pointer" },
  qty: { minWidth: 28, textAlign: "center", fontWeight: 800, fontSize: 18 },
  clear: { border: "none", background: "none", color: "#EF4444", cursor: "pointer", fontSize: 13 },
  divider: { height: 1, background: t.divider, margin: "8px 0" },
  inline: { display: "flex", gap: 8, padding: "0 18px 12px" },
  input: { flex: 1, height: 46, borderRadius: 10, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.inputText, padding: "0 14px", fontSize: 16, fontWeight: 500, outline: "none", boxSizing: "border-box" },
  inlineApply: { minWidth: 130, height: 46, borderRadius: 10, border: "none", background: t.accent, color: "#fff", fontWeight: 500, cursor: "pointer" },
  taxPanel: { padding: "0 18px 12px", display: "flex", flexDirection: "column", gap: 8 },
  taxOpt: { textAlign: "left", padding: "10px 12px", borderRadius: 10, border: "1px solid", background: t.surfaceAlt || "transparent", fontWeight: 500, cursor: "pointer" },
  muted: { color: t.textMuted, fontSize: 13 },
  actions: { padding: "8px 18px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, borderTop: `1px solid ${t.divider}` },
  act: { height: 44, borderRadius: 10, border: "none", background: t.accent, color: "#fff", fontWeight: 500, fontSize: 14, cursor: "pointer" },
  actOff: { height: 44, borderRadius: 10, border: "none", background: "#9AA3B2", color: "#E5E7EB", fontWeight: 500, fontSize: 14, cursor: "not-allowed", opacity: 0.6 }, // promo-locked (Android white_gray2)
  foot: { padding: "8px 18px 16px", display: "flex", gap: 10 },
  cancel: { flex: 1, height: 48, borderRadius: 12, border: `1px solid ${t.cardBorder}`, background: "transparent", color: t.text, fontWeight: 500, cursor: "pointer" },
  done: { flex: 1, height: 48, borderRadius: 12, border: "none", background: "#059669", color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer" },
});
